import { useEffect, useRef, useState } from "react";
import { Image } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { UploadService } from "@/services/APIs";
import CustomButton from "../common/Button";
import { Trash } from "lucide-react";
import { useParams } from "react-router-dom";

type CustomImageUploadProps = {
  folderName?: string;
  onUploaded?: (response: any, file: File) => void;
  width?: number; // preferred simple width alias (px)
  boxWidth?: number;
  boxHeight?: number;
  disabled?: boolean;
  accept?: string;
  shape?: "square" | "circle";
  borderStyle?: "solid" | "dashed" | "dotted" | "double" | "hidden";
  multiple?: boolean;
  value?:
    | string
    | { id: string; url: string; name: string; size: number }
    | string[]
    | { id: string; url: string; name: string; size: number }[]
    | null;
  onChange?: (
    value:
      | string
      | { id: string; url: string; name: string; size: number }
      | string[]
      | { id: string; url: string; name: string; size: number }[]
      | null
  ) => void;
  initialUrl?: string;
  className?: string;
};

export const CustomImageUpload: React.FC<CustomImageUploadProps> = ({
  folderName = "images",
  onUploaded,
  width,
  boxWidth,
  boxHeight,
  disabled = false,
  accept = "image/*",
  borderStyle = "dashed",
  className,
  value,
  onChange,
  initialUrl,
  multiple = false,
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const DEFAULT_SIZE = 160;
  const W = width ?? boxWidth ?? DEFAULT_SIZE;
  const H = boxHeight ?? DEFAULT_SIZE;
  const { id } = useParams();
  const isEditMode = !!id;

  // states
  const [previewUrl, setPreviewUrl] = useState<string | null>(
    initialUrl ?? null
  );
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);
  const [isObjectPreview, setIsObjectPreview] = useState<boolean>(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isHovered, setIsHovered] = useState<number | null>(null);
  const [fullImageUrl, setFullImageUrl] = useState<string | null>(null);

  // Load existing files (edit mode)
  useEffect(() => {
    const fetchFiles = async () => {
      if (!isEditMode || !value) {
        return;
      }

      if (multiple && Array.isArray(value)) {
        const urls: string[] = [];
        for (const val of value) {
          const url = typeof val === "string" ? val : val?.url;
          const response = await UploadService.getFileById({
            id: url as string,
          });
          urls.push(response?.file_path_thumbnail ?? response?.file_path);
        }
        setPreviewUrls(urls);
      } else if (!multiple) {
        let url: string;
        if (typeof value === "string") {
          url = value;
        } else if (value && typeof value === "object" && "url" in value) {
          url = value.url;
        } else {
          return;
        }
        const response = await UploadService.getFileById({ id: url as string });
        setPreviewUrl(response?.file_path_thumbnail ?? response?.file_path);
        setFullImageUrl(response?.file_path);
      }
    };
    fetchFiles();
  }, [value, isEditMode, multiple]);

  // cleanup
  useEffect(() => {
    return () => {
      if (isObjectPreview && previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [isObjectPreview, previewUrl]);

  const openPicker = () => {
    if (!disabled) {
      inputRef.current?.click();
    }
  };

  const extractUrl = (res: any): string | null =>
    res?.[0]?.file_path ??
    res?.data?.[0]?.url ??
    res?.files?.[0]?.url ??
    res?.url ??
    null;

  const extractId = (res: any): string | null =>
    res?.[0]?.id ??
    res?.data?.[0]?.id ??
    res?.files?.[0]?.id ??
    res?.id ??
    null;

  // ✅ Handle Upload
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files?.length) {
      return;
    }

    if (multiple) {
      // MULTIPLE UPLOAD FIXED
      const uploadedIds: string[] = [];
      const uploadedUrls: string[] = [];

      for (const file of Array.from(files)) {
        if (!file.type.startsWith("image/")) {
          continue;
        }

        try {
          const response = await UploadService.uploadFiles({
            folderName,
            files: [file],
          });

          const finalUrl = extractUrl(response);
          const fileId = extractId(response);

          if (finalUrl) {
            uploadedUrls.push(finalUrl);
          }
          if (fileId) {
            uploadedIds.push(fileId);
          }

          onUploaded?.(response, file);
        } catch (error) {
          console.error("Upload failed:", error);
        }
      }

      // 👇 Only append final URLs once
      setPreviewUrls([...previewUrls, ...uploadedUrls]);
      onChange?.(uploadedIds);
    } else {
      // SINGLE UPLOAD
      const file = files[0];
      if (!file.type.startsWith("image/")) {
        e.target.value = "";
        return;
      }

      if (isObjectPreview && previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      const tempUrl = URL.createObjectURL(file);
      setSelectedFile(file);
      setPreviewUrl(tempUrl);
      setIsObjectPreview(true);

      try {
        const response = await UploadService.uploadFiles({
          folderName,
          files: [file],
        });

        const finalUrl = extractUrl(response);
        const fileId = extractId(response);

        if (finalUrl) {
          if (isObjectPreview && previewUrl) {
            URL.revokeObjectURL(previewUrl);
          }
          setPreviewUrl(finalUrl);
          setIsObjectPreview(false);
          onChange?.(fileId ?? null);
          onUploaded?.(response, file);
        }
      } catch (error) {
        console.error("Upload failed:", error);
      }
    }

    e.target.value = "";
  };

  const clearSelection = (index?: number) => {
    if (multiple) {
      const newUrls = previewUrls.filter((_, i) => i !== index);
      setPreviewUrls(newUrls);
      onChange?.(newUrls);
    } else {
      if (isObjectPreview && previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      setSelectedFile(null);
      setPreviewUrl(null);
      setIsObjectPreview(false);
      onChange?.(null);
    }
  };

  // border styles
  const borderStyleClass =
    borderStyle === "dashed"
      ? "border-dashed"
      : borderStyle === "dotted"
        ? "border-dotted"
        : borderStyle === "double"
          ? "border-double"
          : borderStyle === "hidden"
            ? "border-none"
            : "";

  const borderWidthClass = borderStyle === "hidden" ? "border-0" : "border-2";
  const borderColorClass =
    borderStyle === "hidden" ? "" : "border-gray-300 hover:border-gray-400";

  // ✅ Conditional render
  return (
    <div className={`space-y-2 ${className}`}>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        onChange={handleFileChange}
        style={{ display: "none" }}
      />

      {multiple ? (
        <div className="flex flex-wrap gap-3">
          {previewUrls.map((url, index) => (
            <div
              key={url}
              className="relative"
              style={{ width: W, height: H }}
              onMouseEnter={() => setIsHovered(index)}
              onMouseLeave={() => setIsHovered(null)}
            >
              <Image
                src={url}
                alt={`img-${index}`}
                width={W}
                height={H}
                style={{
                  objectFit: "cover",
                  borderRadius: 8,
                  border: "1px dashed #d9d9d9",
                }}
              />
              {isHovered === index && (
                <CustomButton
                  danger
                  icon={<Trash size={16} />}
                  type="text"
                  onClick={() => clearSelection(index)}
                  className="absolute right-1 top-1 rounded-full bg-white p-2 text-red-500 shadow"
                />
              )}
            </div>
          ))}

          {!disabled && (
            <div
              onClick={openPicker}
              className={[
                "flex cursor-pointer items-center justify-center rounded-md text-gray-400 transition hover:text-gray-600",
                borderWidthClass,
                borderStyleClass,
                borderColorClass,
              ].join(" ")}
              style={{ width: W, height: H }}
            >
              <PlusOutlined style={{ fontSize: 28 }} />
            </div>
          )}
        </div>
      ) : !previewUrl ? (
        <div
          role="button"
          tabIndex={0}
          onClick={openPicker}
          className={[
            "flex cursor-pointer items-center justify-center rounded-md text-gray-400 transition hover:text-gray-600",
            borderWidthClass,
            borderStyleClass,
            borderColorClass,
          ].join(" ")}
          style={{ width: W, height: H }}
        >
          <PlusOutlined style={{ fontSize: 28 }} />
        </div>
      ) : (
        <div
          className="relative cursor-pointer"
          style={{ width: W, height: H }}
          onMouseEnter={() => setIsHovered(0)}
          onMouseLeave={() => setIsHovered(null)}
        >
          <Image
            src={previewUrl}
            alt={selectedFile?.name ?? "image"}
            width={W}
            height={H}
            style={{
              objectFit: "cover",
              borderRadius: 8,
              border: "1px dashed #d9d9d9",
            }}
            preview={{
              mask: <span style={{ fontSize: 16 }}>Preview</span>,
              src: fullImageUrl ?? previewUrl,
            }}
          />
          {isHovered === 0 && (
            <CustomButton
              danger
              icon={<Trash size={16} />}
              type="text"
              onClick={() => clearSelection()}
              className="absolute right-1 top-1 rounded-full bg-white p-2 text-red-500 shadow"
            />
          )}
        </div>
      )}
    </div>
  );
};
